import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SideSheet from "@/components/SideSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DetailSkeleton } from "@/components/Skeletons";
import { fmtDate } from "@/lib/dates";
import { plural } from "@/lib/utils";
import { ExternalLink } from "lucide-react";

const RECENT = 6;

/**
 * What a trainer has actually done.
 *
 * A trainer was a name in a dropdown: the row carried a type, an organisation and an
 * expertise area, and nothing that happened. "How many sessions has Ritvighya taken" —
 * a colleague who is on the employee register, is an internal trainer, and has delivered
 * sessions — could not be answered anywhere in the product.
 *
 * Sessions means DELIVERED, so a scheduled one does not count yet. People trained counts
 * distinct people marked Present; seats counts attendances, and the two are different
 * questions — somebody attending three of her sessions is one person trained, not three.
 */
function Row({ label, children }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <div className="w-32 shrink-0 text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Figure({ value, label }) {
  return (
    <div className="rounded-md border border-border p-3 text-center">
      <div className="font-heading font-bold text-xl">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function TrainerPeek({ id, onClose }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    setD(null);
    api.get(`/trainers/${id}/history`)
      .then((r) => { if (live) setD(r.data); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose?.(); });
    return () => { live = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = d?.trainer;
  const recent = (d?.trainings || []).slice(0, RECENT);

  return (
    <SideSheet open={!!id} onOpenChange={(v) => !v && onClose()} title={t?.name || "Trainer"}
               description={t ? [t.type, t.organization].filter(Boolean).join(" · ") : "Loading"}
               testid="trainer-peek"
               footer={t && (
                 <Button onClick={() => nav(`/trainings?trainer_id=${t.id}`)} data-testid="peek-trainer-trainings">
                   <ExternalLink className="w-4 h-4 mr-1.5" /> Their trainings
                 </Button>
               )}>
      {!d ? <DetailSkeleton testid="trainer-peek-skeleton" /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Figure value={d.sessions} label="sessions delivered" />
            <Figure value={d.hours} label="hours delivered" />
            <Figure value={d.people_trained} label="people trained" />
            <Figure value={d.seats_delivered} label="seats filled" />
          </div>

          <section>
            <div className="overline text-muted-foreground mb-1">Who they are</div>
            <Row label="Type">{t.type === "internal" ? "Internal — a colleague" : "External"}</Row>
            <Row label="Organisation">{t.organization || "—"}</Row>
            <Row label="Expertise">{t.expertise_area || "—"}</Row>
            <Row label="Contact">
              {[t.contact_email, t.contact_phone].filter(Boolean).join(" · ") || "—"}
            </Row>
          </section>

          <section>
            <div className="overline text-muted-foreground mb-1">What they have covered</div>
            <Row label="Categories">
              {(d.categories || []).length
                ? d.categories.map((c) => <Badge key={c} variant="secondary" className="mr-1">{c}</Badge>)
                : "—"}
            </Row>
            <Row label="Units">{(d.units || []).join(", ") || "—"}</Row>
            <Row label="First session">{fmtDate(d.first_session) || "—"}</Row>
            <Row label="Last session">{fmtDate(d.last_session) || "—"}</Row>
          </section>

          <section>
            <div className="overline text-muted-foreground mb-1">
              Recent sessions{d.trainings.length > RECENT && ` · ${plural(d.trainings.length, "in total")}`}
            </div>
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing recorded against them yet.</p>
            ) : recent.map((tr) => (
              <button key={tr.id} type="button" onClick={() => nav(`/trainings/${tr.id}`)}
                      className="w-full text-left py-1.5 border-b border-border last:border-0 hover:bg-muted/40 rounded-sm px-1">
                <div className="text-sm font-medium truncate">{tr.title}</div>
                <div className="text-xs text-muted-foreground">
                  {[fmtDate(tr.training_date), tr.category, tr.workflow_status].filter(Boolean).join(" · ")}
                </div>
              </button>
            ))}
          </section>
        </div>
      )}
    </SideSheet>
  );
}
